import { Box, Button, Stack } from '@mui/material';
import { FC } from 'react';

import { AbgTypography, useTranslation } from '@abg/kit-base';
import { usePageContext } from '@abg/web-core/context/page/client-provider';

import { useIsAvisFirstRental } from '../../hooks/use-is-avis-first-rental';
import { getFormattedDateTimeString } from '../../shared/utils/get-formated-date-time-string';
import { userStoreSelector } from '../../store';
import { AemImage } from '../AemImage';

import {
  accountTodayRentalContainerStyles,
  circlePlusButtonStyles,
  vehicleImageContainerStyles,
} from './account-today-rental.styling';
import { AccountTodayRentalProps } from './account-today-rental.types';

import BudgetFeaturedCarImage from '../../../assets/blue_featured.png';
import CirclePlusButtonImage from '../../../assets/circle-plus-button.svg';
import AvisFeaturedCarImage from '../../../assets/red_featured.png';

const images = {
  avis: () => AvisFeaturedCarImage,
  budget: () => BudgetFeaturedCarImage,
  budgettruck: () => BudgetFeaturedCarImage,
};

const AccountTodayRental: FC<AccountTodayRentalProps> = ({
  i18n,
  currentRental,
}) => {
  const { brand, navigationLinks, features } = usePageContext();

  const t = useTranslation(i18n);
  const lastName =
    userStoreSelector.use.userProfile()?.personalDetails?.name?.lastName;

  const { isAvisFirst: isAvisFirstBooking, isLoading: isAvisFirstLoading } =
    useIsAvisFirstRental(currentRental?.confirmationNumber, lastName);

  const shouldShowComponent =
    currentRental &&
    lastName &&
    !isAvisFirstLoading &&
    !features['feature.preferredFastbreakChoiceDisabled'];

  if (!shouldShowComponent) {
    return null;
  }

  const isActiveRental = !!currentRental?.rentalAgreement?.number;

  const viewDetailsLink = (() => {
    if (isActiveRental) {
      const params = new URLSearchParams({
        p: currentRental.rentalAgreement!.number!,
        confirmationNumber: currentRental.confirmationNumber!,
        lastName: lastName,
      });
      if (currentRental.rentalAgreement?.prefix) {
        params.set('prefix', currentRental.rentalAgreement.prefix);
      }
      return `${navigationLinks.rentalDashboard}?${params.toString()}`;
    }
    return `${navigationLinks.reservationDetails}?confirmationNumber=${currentRental?.confirmationNumber}&lastName=${lastName}`;
  })();

  const chooseMyVehicleLink = `${navigationLinks.preRentalDashboard}?confirmationNumber=${currentRental?.confirmationNumber}&lastName=${lastName}`;

  return (
    <Stack gap={1.5} sx={accountTodayRentalContainerStyles}>
      <AbgTypography variant="bodyRegularBold">
        {isAvisFirstBooking
          ? t('rentalDashboard.todaysRental.label')
          : t('rentalDashboard.selectTodaysRental.label')}
      </AbgTypography>
      <Stack gap={2}>
        <AbgTypography variant="h4">
          {currentRental?.pickUpLocation?.locationDetails?.name}
        </AbgTypography>
        <Stack gap={0.5}>
          <AbgTypography variant="bodyRegularMedium">
            {getFormattedDateTimeString(
              "MMM dd, yyyy 'at' hh:mmaaa",
              currentRental?.pickUpDateTime?.date,
              currentRental?.pickUpDateTime?.time,
            )}
          </AbgTypography>
          <AbgTypography variant="bodyRegularMedium">
            {t('rentalDashboard.confirmationNumber.label', {
              id: currentRental?.confirmationNumber,
            })}
          </AbgTypography>
        </Stack>
      </Stack>
      <Stack flexDirection="row" justifyContent="center">
        <Button
          sx={vehicleImageContainerStyles}
          component={isAvisFirstBooking ? 'button' : 'a'}
          href={isAvisFirstBooking ? undefined : chooseMyVehicleLink}
          disabled={isAvisFirstBooking}
          data-testid="account-today-rental-choose-vehicle-image-button"
        >
          <AemImage
            key="featured-car"
            src={images[brand]()}
            alt={`${brand} featured car`}
            width={245}
            height={126}
            data-testid="account-today-rental-choose-vehicle-button-image"
          />
          <Box sx={circlePlusButtonStyles}>
            <AemImage
              key="circle-plus-button"
              src={CirclePlusButtonImage}
              alt="Circle Plus Button"
              width={50}
              height={50}
              data-testid="account-today-rental-choose-vehicle-button-image-circle-plus"
            />
          </Box>
        </Button>
      </Stack>
      <Stack gap={1}>
        {!isAvisFirstBooking && (
          <Button
            variant="contained"
            className="primary"
            component="a"
            href={chooseMyVehicleLink}
            data-testid="account-today-rental-choose-vehicle-button"
          >
            {t('rentalDashboard.chooseMyVehicle.label')}
          </Button>
        )}
        <Button
          variant={isAvisFirstBooking ? 'contained' : 'outlined'}
          className={isAvisFirstBooking ? 'primary' : 'secondary'}
          component="a"
          href={viewDetailsLink}
          data-testid="account-today-rental-view-details-button"
        >
          {isAvisFirstBooking
            ? t('rentals.viewDetails.placeholder.label')
            : t('global.viewDetails.placeholder.label')}
        </Button>
      </Stack>
    </Stack>
  );
};

export default AccountTodayRental;
